import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader, LoadingState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { User, Upload, Camera, Pen, LockKey, ShieldWarning, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function UserProfile() {
    const { user: authUser, setUser: setAuthUser, logout } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [photoFile, setPhotoFile] = useState(null);
    const [signatureFile, setSignatureFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [signaturePreview, setSignaturePreview] = useState(null);
    const [passwordData, setPasswordData] = useState({
        current_password: "",
        new_password: "",
        confirm_password: ""
    });
    const [changingPassword, setChangingPassword] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletePassword, setDeletePassword] = useState("");
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        loadProfile();
    }, []);

    async function loadProfile() {
        setLoading(true);
        try {
            const response = await api.get("/users/me");
            setProfile(response.data);
            setPhotoPreview(response.data.photo_url);
            setSignaturePreview(response.data.signature_url);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    }

    function handlePhotoChange(e) {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.match(/^image\/(png|jpeg|jpg)$/)) {
                toast.error("Please upload PNG or JPG image only");
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                toast.error("Image size must be less than 5MB");
                return;
            }
            setPhotoFile(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    }

    function handleSignatureChange(e) {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.match(/^image\/(png|jpeg|jpg)$/)) {
                toast.error("Please upload PNG or JPG image only");
                return;
            }
            if (file.size > 2 * 1024 * 1024) {
                toast.error("Signature image must be less than 2MB");
                return;
            }
            setSignatureFile(file);
            setSignaturePreview(URL.createObjectURL(file));
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setSaving(true);

        try {
            // Upload photo if changed
            let photoUrl = profile.photo_url;
            if (photoFile) {
                const formData = new FormData();
                formData.append("file", photoFile);
                const photoRes = await api.post("/upload/photo", formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                photoUrl = photoRes.data.url;
            }

            // Upload signature if changed
            let signatureUrl = profile.signature_url;
            if (signatureFile) {
                const formData = new FormData();
                formData.append("file", signatureFile);
                const sigRes = await api.post("/upload/signature", formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
                signatureUrl = sigRes.data.url;
            }

            // Update profile
            const updatedData = {
                name: profile.name,
                email: profile.email,
                phone: profile.phone,
                occupancy_type: profile.occupancy_type,
                photo_url: photoUrl,
                signature_url: signatureUrl
            };

            const response = await api.put("/users/me", updatedData);
            setProfile(response.data);
            
            // Update auth context
            setAuthUser({ ...authUser, ...response.data });
            
            toast.success("Profile updated successfully!");
            setPhotoFile(null);
            setSignatureFile(null);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setSaving(false);
        }
    }

    async function handlePasswordChange(e) {
        e.preventDefault();
        
        // Validation
        if (passwordData.new_password !== passwordData.confirm_password) {
            toast.error("New passwords do not match");
            return;
        }
        
        if (passwordData.new_password.length < 8) {
            toast.error("Password must be at least 8 characters long");
            return;
        }
        
        setChangingPassword(true);
        try {
            await api.post("/users/me/change-password", {
                current_password: passwordData.current_password,
                new_password: passwordData.new_password
            });
            
            toast.success("Password changed successfully!");
            setAuthUser({ ...authUser, must_change_password: false });
            setPasswordData({
                current_password: "",
                new_password: "",
                confirm_password: ""
            });
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setChangingPassword(false);
        }
    }

    if (loading) return <LoadingState />;

    async function handleDeleteAccount(e) {
        e.preventDefault();
        if (!deletePassword) {
            toast.error("Enter your password to confirm");
            return;
        }
        setDeleting(true);
        try {
            await api.post("/users/me/delete", { password: deletePassword });
            toast.success("Your account has been deleted.");
            setDeleteDialogOpen(false);
            await logout();
            navigate("/login");
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setDeleting(false);
        }
    }

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Settings"
                title="My Profile"
                subtitle="Update your personal information, photo, and digital signature"
            />

            <div className="max-w-3xl">
                <Card className="p-6 space-y-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Profile Photo */}
                    <div className="space-y-4">
                        <Label className="text-base font-semibold">Profile Photo</Label>
                        <div className="flex items-center gap-6">
                            <div className="w-24 h-24 rounded-full bg-gray-100 border-2 border-brand-line flex items-center justify-center overflow-hidden">
                                {photoPreview ? (
                                    <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
                                ) : (
                                    <User size={40} className="text-gray-400" />
                                )}
                            </div>
                            <div>
                                <input
                                    type="file"
                                    id="photo-upload"
                                    accept="image/png,image/jpeg,image/jpg"
                                    onChange={handlePhotoChange}
                                    className="hidden"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => document.getElementById('photo-upload').click()}
                                    className="rounded-full"
                                >
                                    <Camera size={16} className="mr-2" />
                                    Upload Photo
                                </Button>
                                <p className="text-xs text-brand-inkSoft mt-2">PNG or JPG, max 5MB</p>
                            </div>
                        </div>
                    </div>

                    {/* Digital Signature */}
                    <div className="space-y-4 border-t border-brand-line pt-6">
                        <Label className="text-base font-semibold">Digital Signature</Label>
                        <div className="flex items-center gap-6">
                            <div className="w-48 h-24 bg-gray-50 border-2 border-dashed border-brand-line rounded-sm flex items-center justify-center overflow-hidden">
                                {signaturePreview ? (
                                    <img src={signaturePreview} alt="Signature" className="max-w-full max-h-full object-contain" />
                                ) : (
                                    <Pen size={32} className="text-gray-400" />
                                )}
                            </div>
                            <div>
                                <input
                                    type="file"
                                    id="signature-upload"
                                    accept="image/png,image/jpeg,image/jpg"
                                    onChange={handleSignatureChange}
                                    className="hidden"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => document.getElementById('signature-upload').click()}
                                    className="rounded-full"
                                >
                                    <Upload size={16} className="mr-2" />
                                    Upload Signature
                                </Button>
                                <p className="text-xs text-brand-inkSoft mt-2">
                                    PNG or JPG, max 2MB. Will appear on official documents.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Personal Information */}
                    <div className="space-y-4 border-t border-brand-line pt-6">
                        <Label className="text-base font-semibold">Personal Information</Label>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Name *</Label>
                                <Input
                                    required
                                    value={profile?.name || ""}
                                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                    placeholder="Your full name"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Email *</Label>
                                <Input
                                    required
                                    type="email"
                                    value={profile?.email || ""}
                                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                    placeholder="your.email@example.com"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Phone Number</Label>
                                <Input
                                    value={profile?.phone || ""}
                                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                                    placeholder="1234567890"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Role</Label>
                                <Input
                                    value={profile?.role || ""}
                                    disabled
                                    className="bg-gray-50 capitalize"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Occupancy Type</Label>
                                {authUser?.role === "resident" ? (
                                    <Input
                                        value={profile?.occupancy_type === "tenant" ? "Tenant" : "Owner"}
                                        disabled
                                        className="bg-gray-50 capitalize"
                                    />
                                ) : (
                                    <Select
                                        value={profile?.occupancy_type || "owner"}
                                        onValueChange={(value) => setProfile({ ...profile, occupancy_type: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="owner">Owner</SelectItem>
                                            <SelectItem value="tenant">Tenant</SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                                {authUser?.role === "resident" && (
                                    <p className="text-xs text-brand-inkSoft">
                                        Contact admin to update occupancy type
                                    </p>
                                )}
                            </div>

                            {profile?.flat_id && (
                                <div className="space-y-2">
                                    <Label>Assigned Flat</Label>
                                    <Input
                                        value={profile?.flat?.block && profile?.flat?.number 
                                            ? `${profile.flat.block}-${profile.flat.number}` 
                                            : "Not assigned"}
                                        disabled
                                        className="bg-gray-50"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end pt-4 border-t border-brand-line">
                        <Button
                            type="submit"
                            disabled={saving}
                            className="bg-brand-action hover:bg-brand-actionHover text-white rounded-full px-8"
                        >
                            {saving ? "Saving..." : "Save Changes"}
                        </Button>
                    </div>
                </form>
                </Card>

                {/* Change Password Section */}
                <Card className="p-6 space-y-6 mt-6">
                    <div className="flex items-center gap-3">
                        <LockKey size={24} className="text-brand-ink" />
                        <div>
                            <Label className="text-base font-semibold">Change Password</Label>
                            <p className="text-sm text-brand-inkSoft">Update your password to keep your account secure</p>
                        </div>
                    </div>

                    <form onSubmit={handlePasswordChange} className="space-y-4 border-t border-brand-line pt-6">
                        <div className="space-y-2">
                            <Label>Current Password *</Label>
                            <Input
                                type="password"
                                required
                                value={passwordData.current_password}
                                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                                placeholder="Enter current password"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>New Password *</Label>
                                <Input
                                    type="password"
                                    required
                                    minLength={8}
                                    value={passwordData.new_password}
                                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                                    placeholder="Min 8 characters"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Confirm New Password *</Label>
                                <Input
                                    type="password"
                                    required
                                    minLength={8}
                                    value={passwordData.confirm_password}
                                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                                    placeholder="Re-enter new password"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end pt-2">
                            <Button
                                type="submit"
                                disabled={changingPassword}
                                variant="outline"
                                className="rounded-full px-8"
                            >
                                {changingPassword ? "Changing..." : "Change Password"}
                            </Button>
                        </div>
                    </form>
                </Card>

                {/* Privacy & Account Data Section */}
                <Card className="p-6 space-y-6 mt-6" data-testid="privacy-account-section">
                    <div className="flex items-center gap-3">
                        <ShieldWarning size={24} className="text-brand-ink" />
                        <div>
                            <Label className="text-base font-semibold">Privacy &amp; Account Data</Label>
                            <p className="text-sm text-brand-inkSoft">Review our privacy practices or permanently delete your account</p>
                        </div>
                    </div>
                    <div className="border-t border-brand-line pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer"
                            data-testid="profile-privacy-policy-link"
                            className="inline-flex items-center gap-2 text-sm font-medium text-brand-action hover:underline">
                            <FileText size={18} /> View Privacy Policy
                        </a>
                        <Button type="button" variant="outline" data-testid="open-delete-account-dialog"
                            onClick={() => setDeleteDialogOpen(true)}
                            className="rounded-full px-6 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700">
                            Delete My Account
                        </Button>
                    </div>
                </Card>
            </div>

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent data-testid="delete-account-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Delete your account?</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleDeleteAccount} className="space-y-4">
                        <p className="text-sm text-brand-inkSoft">
                            This permanently deletes or irreversibly anonymizes your name, email, phone and photo
                            from Maintayn and disables your login immediately — this can't be undone. A narrow set
                            of financial, legal and security records tied to your flat are kept in de-identified
                            form for your society's accounting/legal obligations, but will no longer show your
                            personal details. Read the full <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-brand-action underline">Privacy Policy</a>.
                        </p>
                        <div className="space-y-2">
                            <Label>Enter your password to confirm *</Label>
                            <Input type="password" required value={deletePassword}
                                data-testid="delete-account-password-input"
                                onChange={(e) => setDeletePassword(e.target.value)} placeholder="Your password" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" className="rounded-full" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={deleting} data-testid="confirm-delete-account-button"
                                className="rounded-full bg-red-600 hover:bg-red-700 text-white">
                                {deleting ? "Deleting…" : "Permanently Delete Account"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
